import React, { useEffect, useMemo, useState } from 'react';


function normalizeOption(option) {
    if (option == null) {
        return null;
    }

    if (typeof option === 'string') {
        return {
            rawValue: option,
            value: String(option),
            label: option,
            meta: '',
        };
    }

    const rawValue = option.value ?? '';
    return {
        rawValue,
        value: String(rawValue),
        label: option.label ?? String(rawValue),
        meta: option.meta ?? '',
    };
}


function normalizeText(value) {
    return String(value || '').trim().toLocaleLowerCase('ru-RU');
}


export default function SearchableSelect({
    value,
    options,
    onChange,
    onSearchChange,
    placeholder = 'Начни вводить...',
    disabled = false,
    loading = false,
    noResultsText = 'Ничего не найдено',
    className = '',
    onFocus,
}) {
    const normalizedOptions = useMemo(
        () => (options || []).map(normalizeOption).filter(Boolean),
        [options],
    );

    const selectedOption = useMemo(
        () => normalizedOptions.find((option) => option.value === String(value ?? '')) || null,
        [normalizedOptions, value],
    );
    const selectedLabel = selectedOption?.label || '';

    const [inputValue, setInputValue] = useState(selectedOption?.label || '');
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setInputValue(selectedLabel);
    }, [selectedLabel]);

    const filteredOptions = useMemo(() => {
        const normalizedSelectedLabel = normalizeText(selectedLabel);
        const currentInput = normalizeText(inputValue);
        const query = currentInput === normalizedSelectedLabel ? '' : currentInput;
        const pool = !query
            ? normalizedOptions
            : normalizedOptions.filter((option) => (
                normalizeText(option.label).includes(query)
                || normalizeText(option.value).includes(query)
                || normalizeText(option.meta).includes(query)
            ));
        return pool;
    }, [inputValue, normalizedOptions, selectedLabel]);

    function commitInput(nextValue) {
        setInputValue(nextValue);
        onSearchChange?.(nextValue);

        const exactMatch = normalizedOptions.find((option) => (
            normalizeText(option.label) === normalizeText(nextValue)
            || normalizeText(option.value) === normalizeText(nextValue)
        ));

        if (exactMatch) {
            onChange(exactMatch.rawValue);
        } else if (!String(nextValue || '').trim()) {
            onChange('');
        }
    }

    function handleSelect(option) {
        setInputValue(option.label);
        onSearchChange?.(option.label);
        onChange(option.rawValue);
        setOpen(false);
    }

    function handleBlur() {
        window.setTimeout(() => {
            setOpen(false);

            const exactMatch = normalizedOptions.find((option) => (
                normalizeText(option.label) === normalizeText(inputValue)
                || normalizeText(option.value) === normalizeText(inputValue)
            ));

            if (!exactMatch) {
                const fallback = selectedOption?.label || '';
                setInputValue(fallback);
                onSearchChange?.(fallback);
            }
        }, 120);
    }

    function clearValue() {
        setInputValue('');
        onSearchChange?.('');
        onChange('');
        setOpen(false);
    }

    return (
        <div className={`shell-searchableSelect ${open ? 'is-open' : ''} ${className}`.trim()}>
            <div className="shell-searchableSelectControl">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(event) => {
                        commitInput(event.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => {
                        onFocus?.();
                        setOpen(true);
                    }}
                    onBlur={handleBlur}
                    onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                            setOpen(false);
                        }
                        if (event.key === 'Enter' && open && filteredOptions.length) {
                            event.preventDefault();
                            handleSelect(filteredOptions[0]);
                        }
                    }}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="shell-searchableSelectInput"
                    autoComplete="off"
                />

                {(selectedOption || inputValue) && !disabled ? (
                    <button
                        type="button"
                        className="shell-searchableSelectClear"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={clearValue}
                        aria-label="Очистить выбор"
                    >
                        x
                    </button>
                ) : null}
            </div>

            {open && !disabled ? (
                <div className="shell-searchableSelectMenu">
                    {loading ? (
                        <div className="shell-searchableSelectEmpty">Ищем...</div>
                    ) : filteredOptions.length ? (
                        filteredOptions.map((option) => (
                            <button
                                key={`${option.value}-${option.label}`}
                                type="button"
                                className={`shell-searchableSelectOption ${selectedOption?.value === option.value ? 'is-selected' : ''}`}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleSelect(option)}
                            >
                                <strong>{option.label}</strong>
                                {option.meta ? <span>{option.meta}</span> : null}
                            </button>
                        ))
                    ) : (
                        <div className="shell-searchableSelectEmpty">{noResultsText}</div>
                    )}
                </div>
            ) : null}
        </div>
    );
}
